import { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Calendar, Clock, Users, Mail, Phone, User, AlertCircle, XCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [partySize, setPartySize] = useState(null); // No default - must be selected
  const [availableTimes, setAvailableTimes] = useState([]);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [restaurantFull, setRestaurantFull] = useState(false);
  const [tooSoonToBook, setTooSoonToBook] = useState(false);
  const [contactPhone, setContactPhone] = useState("0471556751");
  const [minAdvanceHours, setMinAdvanceHours] = useState(2);
  const [lunchEndTime, setLunchEndTime] = useState("17:30"); // Dynamic from backend
  const [closedDates, setClosedDates] = useState([]);
  const [tooSoonSlots, setTooSoonSlots] = useState([]); // Slots that are less than minAdvanceHours away
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedTooSoonTime, setSelectedTooSoonTime] = useState(null);
  
  // Seating preference state
  const [seatingPreference, setSeatingPreference] = useState(null); // 'table' or 'bar'
  const [tablesAvailable, setTablesAvailable] = useState(true);
  const [barAvailable, setBarAvailable] = useState(true);
  const [showBarSuggestion, setShowBarSuggestion] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    special_requests: "",
    allergies: ""
  });
  
  // Early service warning modal state
  const [showEarlyServiceWarning, setShowEarlyServiceWarning] = useState(false);
  const [pendingTimeSelection, setPendingTimeSelection] = useState(null);
  
  // Times that trigger the early service warning
  const earlyServiceTimes = ["17:30", "17:45", "18:00", "18:15", "18:30"];
  
  // Early service warning message
  const earlyServiceMessage = "Daar wij met 2 services werken in de avond willen wij graag meegeven dat we de tafel 2u15 minuten voor jullie reserveren. Gelieve hier rekening mee te houden. Indien jullie graag wensen te blijven zitten na het diner dan raden we aan om de reservaties vanaf 20u30 te nemen. Natuurlijk kunnen jullie na het diner nog verder genieten aan onze bar of op ons terras, als we plaats hebben. Tot binnenkort!";

  // Gluten-free notice popup state
  const [showGlutenNotice, setShowGlutenNotice] = useState(false);
  
  // Gluten-free notice messages in 3 languages
  const glutenNoticeMessages = {
    nl: "Sinds onze overname van Barbaar kunnen wij helaas geen glutenvrije maaltijden meer aanbieden. We kiezen er bewust voor om over ieders gezondheid te waken en geen risico's te nemen. We begrijpen dat dit teleurstellend kan zijn en hopen in de toekomst opnieuw opties te kunnen voorzien. Bedankt voor uw begrip - Team BarBaaR",
    fr: "Depuis la reprise de Barbaar, nous ne sommes malheureusement plus en mesure de proposer des repas sans gluten. Nous faisons ce choix afin de veiller à la santé de chacun et d'éviter tout risque. Nous comprenons que cela puisse être décevant et espérons pouvoir proposer à nouveau des options à l'avenir. Merci pour votre compréhension - Team BarBaaR",
    en: "Since our takeover of Barbaar, we are unfortunately no longer able to offer gluten-free meals. We have made this choice to safeguard everyone's health and avoid any risks. We understand this may be disappointing and hope to offer options again in the future. Thank you for your understanding - Team BarBaaR"
  };

  // Show gluten notice on first visit
  useEffect(() => {
    const hasSeenGlutenNotice = localStorage.getItem('barbaar_gluten_notice_seen');
    if (!hasSeenGlutenNotice) {
      setShowGlutenNotice(true);
    }
  }, []);

  // Dismiss gluten notice
  const dismissGlutenNotice = () => {
    localStorage.setItem('barbaar_gluten_notice_seen', 'true');
    setShowGlutenNotice(false);
  };

  // Helper function for local date formatting (avoids timezone issues)
  const formatDateLocal = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch closed dates for the calendar - refreshes on mount and when window gets focus
  const fetchClosedDates = useCallback(async () => {
    try {
      const today = new Date();
      const startDate = formatDateLocal(today);
      // Fetch 365 days ahead to cover all calendar navigation
      const endDate = formatDateLocal(new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000));
      
      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = Date.now();
      const response = await axios.get(`${API}/closed-dates?start_date=${startDate}&end_date=${endDate}&_=${cacheBuster}`);
      setClosedDates(response.data.closed_dates || []);
    } catch (error) {
      console.error("Error fetching closed dates:", error);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchClosedDates();
  }, [fetchClosedDates]);

  // Refresh closed dates when window regains focus (user returns from backoffice)
  useEffect(() => {
    const handleFocus = () => {
      fetchClosedDates();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchClosedDates]);

  // Auto-refresh closed dates every 5 minutes (in case settings change while page is open)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchClosedDates();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [fetchClosedDates]);

  // Create a Set of closed date strings for quick lookup
  const closedDateStrings = useMemo(() => {
    // Only include dates that are FULLY closed (no specific times, meaning whole day is closed)
    return new Set(closedDates
      .filter(cd => !cd.closed_times || cd.closed_times.length === 0)
      .map(cd => cd.date));
  }, [closedDates]);

  // Function to check if a date is fully closed (not partially)
  const isDateClosed = (date) => {
    // Use local date values to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return closedDateStrings.has(dateStr);
  };
  
  // Check if a date has partial closures (some times closed but not all)
  const isDatePartiallyVolzet = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const closed = closedDates.find(cd => cd.date === dateStr);
    return closed && closed.closed_times && closed.closed_times.length > 0;
  };

  // Get reason for closed date
  const getClosedReason = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const closed = closedDates.find(cd => cd.date === dateStr);
    if (!closed) return null;
    
    // Return the custom message if it's an event
    if (closed.display_type === "event" && closed.custom_message) {
      return closed.custom_message;
    }
    
    // Check if it's volzet
    if (closed.display_type === "volzet" || closed.reason?.includes("Volgeboekt") || closed.reason?.includes("Handmatig")) {
      return i18n.language === 'nl' ? 'Volzet' : 'Fully booked';
    }
    
    return i18n.language === 'nl' ? 'Gesloten' : 'Closed';
  };

  // Get closed date type for styling (volzet, event, or closed)
  const getClosedDateType = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const closed = closedDates.find(cd => cd.date === dateStr);
    if (!closed) return null;
    
    // Check for event type
    if (closed.display_type === "event") {
      return "event";
    }
    
    // Check for volzet
    if (closed.display_type === "volzet" || closed.reason?.includes("Volgeboekt") || closed.reason?.includes("Handmatig")) {
      return "volzet";
    }
    
    return "closed";
  };

  // Get full closed date data for popup
  const getClosedDateData = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return closedDates.find(cd => cd.date === dateStr);
  };

  // Check if date is volzet (for styling) - keep for backwards compatibility
  const isDateVolzet = (date) => {
    return getClosedDateType(date) === "volzet";
  };

  // State for tooltip on hover
  const [hoveredClosedDate, setHoveredClosedDate] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // State for closed date info popup
  const [closedDateInfo, setClosedDateInfo] = useState(null);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'nl' ? 'en' : 'nl');
  };

  const checkAvailability = async (date, size) => {
    try {
      setLoading(true);
      setRestaurantFull(false);
      setTooSoonToBook(false);
      
      const response = await axios.post(`${API}/availability`, {
        date: formatDateLocal(date),
        party_size: size
      });
      
      // Store settings from response
      if (response.data.contact_phone) {
        setContactPhone(response.data.contact_phone);
      }
      if (response.data.min_advance_hours) {
        setMinAdvanceHours(response.data.min_advance_hours);
      }
      if (response.data.lunch_end_time) {
        setLunchEndTime(response.data.lunch_end_time);
      }
      
      // Check if booking is too soon
      const now = new Date();
      const minAdvance = response.data.min_advance_hours || 2;
      const minBookingTime = new Date(now.getTime() + (minAdvance * 60 * 60 * 1000));
      const selectedDateStart = new Date(date);
      selectedDateStart.setHours(0, 0, 0, 0);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      
      // Always show all available times
      setAvailableTimes(response.data.available_times || []);
      setTooSoonToBook(false);
      
      // If booking for today, track which slots are too soon
      if (selectedDateStart.getTime() === todayStart.getTime()) {
        const soonSlots = (response.data.available_times || []).filter(time => {
          const [hours, minutes] = time.split(':').map(Number);
          const slotTime = new Date(date);
          slotTime.setHours(hours, minutes, 0, 0);
          return slotTime <= minBookingTime;
        });
        setTooSoonSlots(soonSlots);
      } else {
        setTooSoonSlots([]);
      }
      
      // Check if restaurant is full
      if (response.data.restaurant_full) {
        setRestaurantFull(true);
        setShowWaitlist(true);
      } else if (response.data.available_times.length === 0) {
        setShowWaitlist(true);
      } else {
        setShowWaitlist(false);
      }
    } catch (error) {
      console.error("Error checking availability:", error);
      toast.error("Error checking availability");
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    // Fix timezone issue: react-day-picker may return a date with UTC midnight
    // which can shift the date when converted. Create a new date using local values.
    if (date) {
      const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
      setSelectedDate(localDate);
      checkAvailability(localDate, partySize);
      setStep(1);
    }
  };

  // Handle click on closed date to show info popup
  const handleClosedDateClick = (date) => {
    if (!isDateClosed(date)) return;
    
    const closedData = getClosedDateData(date);
    const dateType = getClosedDateType(date);
    
    if (closedData) {
      setClosedDateInfo({
        date: date,
        formattedDate: date.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
        type: dateType,
        customMessage: closedData.custom_message || null,
        eventImage: closedData.event_image || null
      });
    }
  };

  const handleTimeSelect = async (time) => {
    // Check if this is an early service time that requires a warning
    if (earlyServiceTimes.includes(time)) {
      setPendingTimeSelection(time);
      setShowEarlyServiceWarning(true);
    } else {
      setSelectedTime(time);
      // Check seating availability
      await checkSeatingAvailability(time);
      setStep(1.5); // Go to seating preference step
    }
  };
  
  // Check seating availability for tables and bar
  const checkSeatingAvailability = async (time) => {
    try {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.get(`${API}/seating-availability`, {
        params: { date: dateStr, time: time, party_size: partySize }
      });
      
      setTablesAvailable(response.data.tables_available);
      setBarAvailable(response.data.bar_available);
      
      // If tables are not available but bar is, show suggestion
      if (!response.data.tables_available && response.data.bar_available) {
        setShowBarSuggestion(true);
      } else {
        setShowBarSuggestion(false);
      }
    } catch (error) {
      console.error("Error checking seating availability:", error);
      // Default to tables available if check fails
      setTablesAvailable(true);
      setBarAvailable(true);
    }
  };
  
  // Handle seating preference selection
  const handleSeatingSelect = (preference) => {
    setSeatingPreference(preference);
    setStep(2); // Go to contact form
  };
  
  // Confirm early service time selection
  const confirmEarlyServiceTime = async () => {
    if (pendingTimeSelection) {
      setSelectedTime(pendingTimeSelection);
      await checkSeatingAvailability(pendingTimeSelection);
      setStep(1.5); // Go to seating preference step
    }
    setShowEarlyServiceWarning(false);
    setPendingTimeSelection(null);
  };
  
  // Cancel early service time selection (choose different time)
  const cancelEarlyServiceTime = () => {
    setShowEarlyServiceWarning(false);
    setPendingTimeSelection(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      toast.error("Naam, e-mail en telefoon zijn verplicht");
      return;
    }

    try {
      setLoading(true);
      
      // Use local date formatting to avoid timezone issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      await axios.post(`${API}/reservations`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: dateStr,
        time: selectedTime,
        party_size: partySize,
        special_requests: formData.special_requests,
        allergies: formData.allergies,
        language: i18n.language,
        source: "website",
        seating_preference: seatingPreference || "table"
      });
      
      toast.success(t('booking.success'));
      setStep(3);
    } catch (error) {
      console.error("Error creating reservation:", error);
      const errorMsg = error.response?.data?.detail || "Error creating reservation";
      
      // Check if slot is full - offer waitlist
      if (errorMsg.includes("vol") || errorMsg.includes("Maximum")) {
        toast.error(errorMsg);
        setShowWaitlist(true);
        setStep(1); // Go back to time selection with waitlist option
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlistSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/waitlist`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: formatDateLocal(selectedDate),
        time: selectedTime || "any",
        party_size: partySize,
        language: i18n.language
      });
      
      toast.success("Added to waitlist!");
      setStep(3);
    } catch (error) {
      console.error("Error joining waitlist:", error);
      toast.error("Error joining waitlist");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_guest-hub-11/artifacts/ry2t7jio_Foto%20Barbaar%202.jpg)',
        }}
      />
      <div className="absolute inset-0 bg-black/60" />
      
      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="p-4 sm:p-6 flex justify-between items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight text-brand-beige" data-testid="site-title">
              {t('home.title')}
            </h1>
            <p className="text-base sm:text-lg text-gray-300 tracking-[0.2em] uppercase" data-testid="site-subtitle">{t('home.subtitle')}</p>
          </div>
          <Button 
            variant="outline" 
            onClick={toggleLanguage}
            className="border-brand-gold text-brand-gold hover:bg-brand-gold/10"
            data-testid="language-toggle-button"
          >
            {i18n.language === 'nl' ? 'EN' : 'NL'}
          </Button>
        </header>

        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
          <div className="w-full max-w-4xl">
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="intro"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center"
                >
                  <p className="text-xl sm:text-2xl text-gray-300 mb-8 tracking-wide" data-testid="tagline">
                    {t('home.tagline')}
                  </p>
                  <Button 
                    onClick={() => setStep(0.5)}
                    className="bg-brand-gold text-black font-sans font-bold tracking-wide py-6 px-8 text-lg hover:bg-[#F0C74A] transition-colors duration-300"
                    data-testid="reserve-button"
                  >
                    {t('home.reserve')}
                  </Button>
                </motion.div>
              )}

              {step === 0.5 && (
                <motion.div
                  key="date"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="bg-[#1A1A1A] border border-brand-gold/30 p-8 max-w-2xl mx-auto"
                >
                  <h2 className="text-2xl sm:text-3xl font-serif text-brand-beige mb-6" data-testid="booking-title">
                    {t('booking.title')}
                  </h2>
                  <div className="mb-6">
                    <label className="text-xs tracking-[0.2em] uppercase text-brand-gold mb-4 block">
                      {t('booking.party_size')}
                    </label>
                    <Select value={partySize ? String(partySize) : ""} onValueChange={(value) => setPartySize(parseInt(value))}>
                      <SelectTrigger className={`w-full max-w-xs bg-[#222222] border-white/20 text-gray-200 h-12 ${!partySize ? 'border-brand-gold' : ''}`} data-testid="party-size-select">
                        <SelectValue placeholder={i18n.language === 'nl' ? 'Selecteer aantal personen *' : 'Select number of guests *'} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#222222] border-white/20">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(size => (
                          <SelectItem 
                            key={size} 
                            value={String(size)}
                            className="text-gray-200 focus:bg-brand-gold/20 focus:text-brand-gold"
                            data-testid={`party-size-${size}`}
                          >
                            {size} {size === 1 
                              ? (i18n.language === 'nl' ? 'persoon' : 'guest') 
                              : (i18n.language === 'nl' ? 'personen' : 'guests')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400 mt-3">
                      {i18n.language === 'nl' 
                        ? <>Indien u een reservatie voor meer dan 8 personen wenst te doen, gelieve dan telefonisch contact op te nemen met ons op{' '}<a href="tel:0471556751" className="text-brand-gold hover:underline">0471556751</a>{' '}of via{' '}<a href="https://wa.me/32471556751" target="_blank" rel="noopener noreferrer" className="text-brand-gold hover:underline">WhatsApp</a>.</>
                        : <>For reservations of more than 8 guests, please contact us by phone at{' '}<a href="tel:0471556751" className="text-brand-gold hover:underline">0471556751</a>{' '}or via{' '}<a href="https://wa.me/32471556751" target="_blank" rel="noopener noreferrer" className="text-brand-gold hover:underline">WhatsApp</a>.</>
                      }
                    </p>
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.2em] uppercase text-brand-gold mb-4 block">
                      {t('booking.date')}
                    </label>
                    
                    {/* Show message when party size not selected */}
                    {!partySize && (
                      <div className="mb-4 p-3 bg-brand-gold/10 border border-brand-gold/30 rounded-lg">
                        <p className="text-brand-gold text-sm">
                          {i18n.language === 'nl' 
                            ? '⬆️ Selecteer eerst het aantal personen hierboven' 
                            : '⬆️ First select the number of guests above'}
                        </p>
                      </div>
                    )}
                    
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        if (!date) return;
                        
                        // Block if party size not selected
                        if (!partySize) {
                          toast.error(i18n.language === 'nl' 
                            ? 'Selecteer eerst het aantal personen' 
                            : 'Please select the number of guests first');
                          return;
                        }
                        
                        // Check if date is closed
                        if (isDateClosed(date)) {
                          handleClosedDateClick(date);
                          return; // Don't select closed dates
                        }
                        
                        // Check if date is in the past
                        if (date < new Date(new Date().setHours(0, 0, 0, 0))) {
                          return; // Don't select past dates
                        }
                        
                        // Normal date selection
                        handleDateSelect(date);
                      }}
                      disabled={(date) => {
                        // Disable all dates if party size not selected
                        if (!partySize) return true;
                        // Only disable past dates with visual style
                        if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
                        return false; // Allow clicking on closed dates to show popup
                      }}
                      modifiers={{
                        closed: (date) => isDateClosed(date)
                      }}
                      modifiersClassNames={{
                        closed: "closed-day"
                      }}
                      components={{
                        DayContent: ({ date }) => {
                          const isClosed = isDateClosed(date);
                          const isPartial = isDatePartiallyVolzet(date);
                          const dateType = isClosed ? getClosedDateType(date) : null;
                          
                          // Determine display text and color
                          let displayText = '';
                          let colorClass = 'text-red-400';
                          
                          if (isClosed) {
                            if (dateType === 'volzet') {
                              displayText = 'VOLZET';
                              colorClass = 'text-amber-500';
                            } else if (dateType === 'event') {
                              displayText = 'EVENT';
                              colorClass = 'text-purple-400';
                            } else {
                              displayText = 'DICHT';
                              colorClass = 'text-red-400';
                            }
                          } else if (isPartial) {
                            // Day is partially closed - some times still available
                            displayText = 'BEPERKT';
                            colorClass = 'text-orange-400';
                          }
                          
                          return (
                            <div 
                              className="relative w-full h-full flex flex-col items-center justify-center group cursor-pointer"
                            >
                              <span className={isClosed ? 'line-through' : ''}>{date.getDate()}</span>
                              {(isClosed || isPartial) && (
                                <span className={`text-[8px] font-bold leading-none mt-0.5 ${colorClass}`}>
                                  {displayText}
                                </span>
                              )}
                            </div>
                          );
                        }
                      }}
                      className="rounded-md border border-white/20 p-4 bg-[#222222] calendar-container"
                      data-testid="date-calendar"
                    />
                    {closedDates.length > 0 && (
                      <div className="mt-3 space-y-1">
                        <div className="flex items-center gap-2 text-xs text-amber-500">
                          <span className="w-3 h-3 rounded-full bg-amber-500/20 border border-amber-500"></span>
                          <span>VOLZET = Volgeboekt</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-orange-400">
                          <span className="w-3 h-3 rounded-full bg-orange-500/20 border border-orange-500"></span>
                          <span>BEPERKT = Enkele uren volzet</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-purple-400">
                          <span className="w-3 h-3 rounded-full bg-purple-500/20 border border-purple-500"></span>
                          <span>EVENT = Speciaal evenement</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-red-400">
                          <span className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500"></span>
                          <span>DICHT = Gesloten</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={() => setStep(0)}
                    variant="outline"
                    className="mt-6 border-brand-gold/30 text-gray-300 hover:bg-white/5"
                    data-testid="back-to-intro-button"
                  >
                    {t('booking.back')}
                  </Button>
                </motion.div>
              )}

              {step === 1 && (
                <motion.div
                  key="time"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="bg-[#1A1A1A] border border-brand-gold/30 p-8 max-w-2xl mx-auto"
                >
                  <h2 className="text-2xl sm:text-3xl font-serif text-brand-beige mb-6" data-testid="select-time-title">
                    {t('booking.time')}
                  </h2>
                  
                  {loading ? (
                    <div className="text-center py-8 text-gray-400">Loading...</div>
                  ) : restaurantFull ? (
                    <div className="text-center py-8" data-testid="restaurant-full-message">
                      <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                      <p className="text-gray-300 mb-2 text-lg font-semibold">
                        {i18n.language === 'nl' 
                          ? 'Het restaurant is volgeboekt voor deze datum.' 
                          : 'The restaurant is fully booked for this date.'}
                      </p>
                      <p className="text-gray-400 mb-6">
                        {i18n.language === 'nl'
                          ? `Neem contact op via WhatsApp op het nummer ${contactPhone} om te kijken of er nog beschikbaarheid is.`
                          : `Please contact us via WhatsApp at ${contactPhone} to check if there's any availability.`}
                      </p>
                      <a 
                        href={`https://wa.me/32471556751`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-brand-gold text-black font-bold px-6 py-3 rounded hover:bg-[#F0C74A] transition-colors"
                        data-testid="call-phone-button"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                      </a>
                    </div>
                  ) : showWaitlist ? (
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 text-brand-gold mx-auto mb-4" />
                      <p className="text-gray-300 mb-6">{t('booking.waitlist_message')}</p>
                      <Button 
                        onClick={() => setStep(2)}
                        className="bg-brand-gold text-black font-bold"
                        data-testid="join-waitlist-button"
                      >
                        {t('booking.join_waitlist')}
                      </Button>
                    </div>
                  ) : availableTimes.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">{t('booking.no_times')}</p>
                  ) : (
                    <div className="space-y-6">
                      {/* Middag sectie - tot 17:00, Avond sectie - vanaf 17:00 */}
                      {(() => {
                        // Split at 17:00 - anything before is lunch, from 17:00 onwards is dinner
                        const dinnerStartMinutes = 17 * 60; // 17:00
                        
                        const lunchTimes = availableTimes.filter(time => {
                          const [h, m] = time.split(':').map(Number);
                          return (h * 60 + m) < dinnerStartMinutes;
                        });
                        
                        const dinnerTimes = availableTimes.filter(time => {
                          const [h, m] = time.split(':').map(Number);
                          return (h * 60 + m) >= dinnerStartMinutes;
                        });
                        
                        return (
                          <>
                            {lunchTimes.length > 0 && (
                              <div>
                                <h3 className="text-sm tracking-[0.15em] uppercase text-brand-gold mb-3 flex items-center gap-2">
                                  <span className="text-lg">☀️</span>
                                  {i18n.language === 'nl' ? 'Middag' : 'Lunch'}
                                </h3>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                  {lunchTimes.map(time => {
                                    const isTooSoon = tooSoonSlots.includes(time);
                                    return (
                                      <button
                                        key={time}
                                        onClick={() => {
                                          if (isTooSoon) {
                                            setSelectedTooSoonTime(time);
                                            setShowWhatsAppModal(true);
                                          } else {
                                            handleTimeSelect(time);
                                          }
                                        }}
                                        className={`py-3 px-4 border transition-colors relative ${
                                          isTooSoon 
                                            ? 'border-amber-500/50 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' 
                                            : 'border-white/20 hover:border-brand-gold hover:bg-brand-gold/10 text-gray-300'
                                        }`}
                                        data-testid={`time-slot-${time.replace(':', '-')}`}
                                      >
                                        {time}
                                        {isTooSoon && (
                                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                                            <svg className="w-2.5 h-2.5 text-black" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                            {dinnerTimes.length > 0 && (
                              <div>
                                <h3 className="text-sm tracking-[0.15em] uppercase text-brand-gold mb-3 flex items-center gap-2">
                                  <span className="text-lg">🌙</span>
                                  {i18n.language === 'nl' ? 'Avond' : 'Dinner'}
                                </h3>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                  {dinnerTimes.map(time => {
                                    const isTooSoon = tooSoonSlots.includes(time);
                                    return (
                                      <button
                                        key={time}
                                        onClick={() => {
                                          if (isTooSoon) {
                                            setSelectedTooSoonTime(time);
                                            setShowWhatsAppModal(true);
                                          } else {
                                            handleTimeSelect(time);
                                          }
                                        }}
                                        className={`py-3 px-4 border transition-colors relative ${
                                          isTooSoon 
                                            ? 'border-amber-500/50 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' 
                                            : 'border-white/20 hover:border-brand-gold hover:bg-brand-gold/10 text-gray-300'
                                        }`}
                                        data-testid={`time-slot-${time.replace(':', '-')}`}
                                      >
                                        {time}
                                        {isTooSoon && (
                                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                                            <svg className="w-2.5 h-2.5 text-black" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  
                  <Button 
                    onClick={() => setStep(0.5)}
                    variant="outline"
                    className="mt-6 border-brand-gold/30 text-gray-300 hover:bg-white/5"
                    data-testid="back-to-date-button"
                  >
                    {t('booking.back')}
                  </Button>
                </motion.div>
              )}

              {/* Step 1.5: Seating Preference */}
              {step === 1.5 && (
                <motion.div
                  key="seating"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="bg-[#1A1A1A] border border-brand-gold/30 p-8 max-w-2xl mx-auto"
                >
                  <h2 className="text-2xl sm:text-3xl font-serif text-brand-beige mb-6">
                    {i18n.language === 'nl' ? 'Waar wilt u zitten?' : 'Where would you like to sit?'}
                  </h2>

                  <div className="space-y-4">
                    {/* Table option */}
                    <button
                      onClick={() => handleSeatingSelect('table')}
                      disabled={!tablesAvailable}
                      className={`w-full p-6 rounded-lg border-2 transition-all text-left ${
                        tablesAvailable 
                          ? 'border-brand-gold/50 hover:border-brand-gold hover:bg-brand-gold/10 cursor-pointer' 
                          : 'border-gray-600 opacity-50 cursor-not-allowed'
                      }`}
                      data-testid="seating-table"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-4xl">🍽️</div>
                        <div>
                          <h3 className="text-xl font-semibold text-brand-beige">
                            {i18n.language === 'nl' ? 'Zaal (Tafel)' : 'Dining Room (Table)'}
                          </h3>
                          <p className="text-gray-400 text-sm mt-1">
                            {tablesAvailable 
                              ? (i18n.language === 'nl' ? 'Zitplaats aan een tafel in de zaal' : 'Seating at a table in the dining room')
                              : (i18n.language === 'nl' ? 'Helaas geen tafels meer beschikbaar' : 'No tables available')}
                          </p>
                        </div>
                        {tablesAvailable && (
                          <div className="ml-auto text-brand-gold">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Bar option */}
                    <button
                      onClick={() => handleSeatingSelect('bar')}
                      disabled={!barAvailable}
                      className={`w-full p-6 rounded-lg border-2 transition-all text-left ${
                        barAvailable 
                          ? 'border-purple-500/50 hover:border-purple-500 hover:bg-purple-500/10 cursor-pointer' 
                          : 'border-gray-600 opacity-50 cursor-not-allowed'
                      }`}
                      data-testid="seating-bar"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-4xl">🍸</div>
                        <div>
                          <h3 className="text-xl font-semibold text-brand-beige">
                            {i18n.language === 'nl' ? 'Bar' : 'Bar'}
                          </h3>
                          <p className="text-gray-400 text-sm mt-1">
                            {barAvailable 
                              ? (i18n.language === 'nl' ? 'Zitplaats aan de bar' : 'Seating at the bar')
                              : (i18n.language === 'nl' ? 'Geen plaatsen aan de bar beschikbaar' : 'No bar seats available')}
                          </p>
                        </div>
                        {barAvailable && (
                          <div className="ml-auto text-purple-400">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Show suggestion when tables are full but bar is available */}
                    {showBarSuggestion && (
                      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg mt-4">
                        <p className="text-purple-300 text-sm">
                          <span className="font-semibold">💡 Tip:</span>{' '}
                          {i18n.language === 'nl' 
                            ? 'Alle tafels zijn bezet, maar er zijn nog plaatsen aan de bar beschikbaar!' 
                            : 'All tables are occupied, but bar seats are still available!'}
                        </p>
                      </div>
                    )}
                  </div>

                  <Button 
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="mt-6 border-brand-gold/30 text-gray-300 hover:bg-white/5"
                    data-testid="back-to-time-button"
                  >
                    {t('booking.back')}
                  </Button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="details"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="bg-[#1A1A1A] border border-brand-gold/30 p-8 max-w-2xl mx-auto"
                >
                  <h2 className="text-2xl sm:text-3xl font-serif text-brand-beige mb-6" data-testid="details-form-title">
                    {showWaitlist ? t('booking.waitlist_title') : t('booking.title')}
                  </h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="text-xs tracking-[0.2em] uppercase text-brand-gold mb-2 block">
                        {t('booking.name')}
                      </label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="bg-transparent border-b border-white/20 rounded-none px-0 py-2 text-primary focus:border-brand-gold focus:ring-0 transition-colors"
                        data-testid="name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs tracking-[0.2em] uppercase text-brand-gold mb-2 block">
                        {t('booking.email')}
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        className="bg-transparent border-b border-white/20 rounded-none px-0 py-2 text-primary focus:border-brand-gold focus:ring-0 transition-colors"
                        data-testid="email-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs tracking-[0.2em] uppercase text-brand-gold mb-2 block">
                        {t('booking.phone')}
                      </label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        required
                        className="bg-transparent border-b border-white/20 rounded-none px-0 py-2 text-primary focus:border-brand-gold focus:ring-0 transition-colors"
                        data-testid="phone-input"
                      />
                    </div>
                    
                    {!showWaitlist && (
                      <>
                        <div>
                          <label className="text-xs tracking-[0.2em] uppercase text-brand-gold mb-2 block">
                            {t('booking.allergies')}
                          </label>
                          <Textarea
                            value={formData.allergies}
                            onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                            placeholder={t('booking.allergies_placeholder')}
                            className="bg-transparent border border-white/20 rounded-none p-3 text-primary focus:border-brand-gold focus:ring-0 transition-colors min-h-[80px]"
                            data-testid="allergies-input"
                          />
                        </div>
                        
                        <div>
                          <label className="text-xs tracking-[0.2em] uppercase text-brand-gold mb-2 block">
                            {t('booking.special_requests')}
                          </label>
                          <Textarea
                            value={formData.special_requests}
                            onChange={(e) => setFormData({...formData, special_requests: e.target.value})}
                            placeholder="Optional"
                            className="bg-transparent border border-white/20 rounded-none p-3 text-primary focus:border-brand-gold focus:ring-0 transition-colors min-h-[80px]"
                            data-testid="special-requests-input"
                          />
                        </div>
                      </>
                    )}
                    
                    <div className="flex gap-4">
                      <Button 
                        type="button"
                        onClick={() => setStep(showWaitlist ? 0.5 : 1.5)}
                        variant="outline"
                        className="flex-1 border-brand-gold/30 text-gray-300 hover:bg-white/5"
                        data-testid="back-button"
                      >
                        {t('booking.back')}
                      </Button>
                      <Button 
                        type={showWaitlist ? "button" : "submit"}
                        onClick={showWaitlist ? handleWaitlistSubmit : undefined}
                        disabled={loading}
                        className="flex-1 bg-brand-gold text-black font-bold hover:bg-[#F0C74A] transition-colors"
                        data-testid="confirm-button"
                      >
                        {loading ? "Processing..." : (showWaitlist ? t('booking.join_waitlist') : t('booking.confirm'))}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#1A1A1A] border border-brand-gold/30 p-8 max-w-2xl mx-auto text-center"
                >
                  <div className="w-16 h-16 bg-brand-gold rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-8 h-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-3xl font-serif text-brand-beige mb-4" data-testid="success-title">
                    {t('booking.success')}
                  </h2>
                  <p className="text-gray-300 mb-8">{t('booking.success_message')}</p>
                  
                  {/* Show early service note on confirmation if applicable */}
                  {earlyServiceTimes.includes(selectedTime) && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6 text-left">
                      <p className="text-amber-200 text-sm leading-relaxed">
                        <Clock className="w-4 h-4 inline mr-2" />
                        {earlyServiceMessage}
                      </p>
                    </div>
                  )}
                  
                  <Button 
                    onClick={() => {
                      setStep(0);
                      setFormData({ name: "", email: "", phone: "", special_requests: "", allergies: "" });
                      setSelectedDate(null);
                      setSelectedTime("");
                    }}
                    className="bg-brand-gold text-black font-bold"
                    data-testid="back-to-home-button"
                  >
                    Back to Home
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        
        {/* Early Service Warning Modal */}
        {showEarlyServiceWarning && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#1A1A1A] border border-brand-gold/30 rounded-lg p-6 max-w-lg w-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-xl font-serif text-brand-beige">Belangrijke Informatie</h3>
              </div>
              
              <p className="text-gray-300 leading-relaxed mb-6">
                {earlyServiceMessage}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={confirmEarlyServiceTime}
                  className="flex-1 bg-brand-gold text-black font-bold hover:bg-[#F0C74A]"
                  data-testid="early-service-confirm"
                >
                  OK, Begrepen
                </Button>
                <Button
                  onClick={cancelEarlyServiceTime}
                  variant="outline"
                  className="flex-1 border-gray-500 text-gray-300 hover:bg-gray-800"
                  data-testid="early-service-choose-other"
                >
                  Ander uur kiezen
                </Button>
              </div>
            </motion.div>
          </div>
        )}
        
        {/* Gluten-Free Notice Modal */}
        {showGlutenNotice && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#1A1A1A] border border-brand-gold/30 rounded-lg p-6 max-w-lg w-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-brand-gold/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-brand-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-serif text-brand-beige">
                  {i18n.language === 'en' ? 'Important Notice' : i18n.language === 'fr' ? 'Avis Important' : 'Belangrijke Mededeling'}
                </h3>
              </div>
              
              <p className="text-gray-300 leading-relaxed mb-6">
                {glutenNoticeMessages[i18n.language] || glutenNoticeMessages.nl}
              </p>
              
              <Button
                onClick={dismissGlutenNotice}
                className="w-full bg-brand-gold text-black font-bold hover:bg-[#F0C74A]"
                data-testid="gluten-notice-ok"
              >
                OK
              </Button>
            </motion.div>
          </div>
        )}

        {/* Closed date info popup */}
        <Dialog open={!!closedDateInfo} onOpenChange={() => setClosedDateInfo(null)}>
          <DialogContent className="bg-[#1A1A1A] border-white/20 text-white max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                {closedDateInfo?.type === 'volzet' ? (
                  <>
                    <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                    <span className="text-amber-400">Volgeboekt</span>
                  </>
                ) : closedDateInfo?.type === 'event' ? (
                  <>
                    <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                    <span className="text-purple-400">Speciaal Evenement</span>
                  </>
                ) : (
                  <>
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="text-red-400">Gesloten</span>
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-2">
              <p className="text-gray-300">
                <span className="text-white font-medium capitalize">{closedDateInfo?.formattedDate}</span>
              </p>
              
              {/* Event image if present */}
              {closedDateInfo?.eventImage && (
                <div className="rounded-lg overflow-hidden border border-white/20 bg-black/20">
                  <img 
                    src={closedDateInfo.eventImage} 
                    alt="Event" 
                    className="w-full max-h-72 object-contain"
                  />
                </div>
              )}
              
              {closedDateInfo?.type === 'volzet' ? (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-amber-400">
                    Het restaurant is helaas volzet op deze datum. Alle tafels zijn gereserveerd.
                  </p>
                </div>
              ) : closedDateInfo?.type === 'event' && closedDateInfo?.customMessage ? (
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-purple-300 whitespace-pre-wrap">
                    {closedDateInfo.customMessage}
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-300">
                    Het restaurant is gesloten op deze datum.
                  </p>
                </div>
              )}

              <p className="text-sm text-gray-400">
                Kies alstublieft een andere datum voor uw reservatie.
              </p>
              
              <Button
                onClick={() => setClosedDateInfo(null)}
                className="w-full bg-brand-gold text-black font-bold hover:bg-[#F0C74A]"
              >
                Sluiten
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* WhatsApp Modal for too-soon bookings */}
        <Dialog open={showWhatsAppModal} onOpenChange={setShowWhatsAppModal}>
          <DialogContent className="bg-[#1A1A1A] border-white/20 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2 text-amber-400">
                <Clock className="w-5 h-5" />
                {i18n.language === 'nl' ? 'Kort vooraf reserveren' : 'Short notice booking'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-2">
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-300">
                  {i18n.language === 'nl' 
                    ? `U wilt reserveren om ${selectedTooSoonTime}. Dit is minder dan ${minAdvanceHours} uur vooraf.`
                    : `You want to book at ${selectedTooSoonTime}. This is less than ${minAdvanceHours} hours in advance.`}
                </p>
              </div>
              
              <p className="text-gray-300">
                {i18n.language === 'nl'
                  ? 'Wij zijn open en hebben mogelijk nog plaats! Neem contact op via WhatsApp om te vragen of er nog beschikbaarheid is.'
                  : 'We are open and may have availability! Contact us via WhatsApp to check if there is still availability.'}
              </p>

              <a 
                href={`https://wa.me/32471556751?text=${encodeURIComponent(
                  i18n.language === 'nl'
                    ? `Hallo, ik zou graag willen reserveren voor vandaag om ${selectedTooSoonTime}. Is er nog beschikbaarheid?`
                    : `Hello, I would like to make a reservation for today at ${selectedTooSoonTime}. Is there still availability?`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 px-6 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors"
                onClick={() => setShowWhatsAppModal(false)}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                {i18n.language === 'nl' ? 'Open WhatsApp' : 'Open WhatsApp'}
              </a>
              
              <Button
                variant="outline"
                onClick={() => setShowWhatsAppModal(false)}
                className="w-full border-white/20 text-gray-300 hover:bg-white/10"
              >
                {i18n.language === 'nl' ? 'Annuleren' : 'Cancel'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default HomePage;// Build timestamp: 1774537030
